/**
 * AMIS Platform Header — apcorejs integration
 * Kiểm soát toàn bộ lifecycle: load dependencies, init APUI, mount UserControlPanel, destroy
 *
 * Tham khảo: https://library.misa.local/2023/12/amis-platform-huong-dan-tich-hop-module-usercontrolpanel-theo-quy-chuan-header/
 */

var _initialized = false;
var _loadTimeout = null;
var _userPanel = null;

var APUI_LOAD_TIMEOUT = 5000;

function isFeatureEnabled() {
	var config = window.__env && window.__env.amisHeaderConfig;
	return config && config.enabled === true && config.libUrl;
}

function getConfig() {
	return window.__env.amisHeaderConfig;
}

/**
 * Load script động, trả về Promise
 */
function loadScript(src) {
	return new Promise(function(resolve, reject) {
		var existing = document.querySelector('script[src="' + src + '"]');
		if (existing) {
			resolve();
			return;
		}

		var script = document.createElement('script');
		script.src = src;
		script.type = 'text/javascript';
		script.onload = function() {
			resolve();
		};
		script.onerror = function() {
			reject(new Error('Failed to load: ' + src));
		};
		document.head.appendChild(script);
	});
}

/**
 * Load CSS động
 */
function loadCss(href) {
	return new Promise(function(resolve, reject) {
		var existing = document.querySelector('link[href="' + href + '"]');
		if (existing) {
			resolve();
			return;
		}

		var link = document.createElement('link');
		link.rel = 'stylesheet';
		link.type = 'text/css';
		link.href = href;
		link.onload = function() {
			resolve();
		};
		link.onerror = function() {
			reject(new Error('Failed to load CSS: ' + href));
		};
		document.head.appendChild(link);
	});
}

function cleanup() {
	if (_loadTimeout) {
		clearTimeout(_loadTimeout);
		_loadTimeout = null;
	}
}

async function setupUserPanel(options) {
	try {
		//tự custom menu
		let userPanelModule = await MISACore.use('UserControlPanel');
		let panel = await userPanelModule.init({
			config: {
				CustomMenu: options.customMenu,
				MobileAds: false,
				InviteBox: true, //sử dụng chức năng mời dùng amis không
				MessageBox: {
					Disable: true
				},
				MenuAccount: {},
				KnowledgeMenu: options.knowledgeConfig,
				NotificationBox: options.notificationConfig,
				HelpMenu: options.helpConfig,
				MoreMenu: options.moreConfig
			},
			mode: options.mode
		});
	} catch (err) {
		console.warn('[AmisHeader] APUI init failed:', err);
		if (options.onFallback) options.onFallback();
	}
}

/**
 * Khởi tạo toàn bộ AMIS header
 * @param {Object} options
 * @param {Object} options.userData - {FullName, TenantID, UserID, MISAID}
 * @param {Function} options.onReady - callback khi mount thành công
 * @param {Function} options.onFallback - callback khi load thất bại
 */
export function initAmisHeader(options) {
	if (!isFeatureEnabled()) {
		if (options && options.onFallback) options.onFallback();
		return;
	}

	if (_initialized) return;

	var cfg = getConfig();

	// Timeout tổng 5s cho toàn bộ chuỗi load
	_loadTimeout = setTimeout(function() {
		console.warn(
			'[AmisHeader] apcorejs load timeout — fallback to MTAX header'
		);
		destroyAmisHeader();
		if (options && options.onFallback) options.onFallback();
	}, APUI_LOAD_TIMEOUT);

	// Chuỗi load tuần tự: jquery → mws.client → apcorejs CSS → apcorejs JS
	Promise.resolve()
		.then(function() {
			return cfg && cfg.mwsUrl
				? loadScript(cfg.mwsUrl)
				: Promise.resolve();
		})
		.then(function() {
			return cfg && cfg.cssUrl ? loadCss(cfg.cssUrl) : Promise.resolve();
		})
		.then(function() {
			return cfg && cfg.libUrl
				? loadScript(cfg.libUrl)
				: Promise.resolve();
		})
		.then(async function() {
			sessionStorage.setItem(
				'misacore_development_userdata',
				JSON.stringify(options.userData)
			);

			let res = await MISACore.Fn.init(
				options.userData,
				options.Role === 1 ? true : false,
				'v1',
				window.__appInfo.ApplicationName
			);

			return res;
		})
		.then(function() {
			clearTimeout(_loadTimeout);
			_initialized = true;
			return setupUserPanel(options);
		})
		.catch(function(err) {
			clearTimeout(_loadTimeout);
			console.warn(
				'[AmisHeader] Failed to load apcorejs dependencies:',
				err
			);
			destroyAmisHeader();
			if (options && options.onFallback) options.onFallback();
		});
}

/**
 * Hủy AMIS header, dọn dẹp resource
 */
export function destroyAmisHeader() {
	cleanup();
	try {
		if (_userPanel && _userPanel.destroy) {
			_userPanel.destroy();
		}
	} catch (e) {
		// silent
	}
	_userPanel = null;
	_initialized = false;
}
